"use client";

import {
  FC,
  HTMLAttributes,
  ReactNode,
  useRef,
  useState,
  useEffect,
} from "react";
import clsx from "clsx";
import { Carousel } from "@mantine/carousel";
import Autoplay from "embla-carousel-autoplay";
import { EmblaCarouselType } from "embla-carousel-react";

import HtmlText from "@/sections/common/HtmlText";

// ------------------------------------------------------------------------------------

export type IRegulation = {
  name?: string;
  description?: ReactNode;
};

export type IRegulationSectionProps = HTMLAttributes<HTMLElement> & {
  heading?: string;
  description?: ReactNode;
  regulations?: IRegulation[];
};

const RegulationSection: FC<IRegulationSectionProps> = (props) => {
  const { heading, description, regulations, className, ...other } = props;

  const [activeTab, setActiveTab] = useState<number>(0);
  const [embla, setEmbla] = useState<EmblaCarouselType | null>(null);

  const autoplay = useRef(
    Autoplay({
      delay: 10000,
      stopOnMouseEnter: true,
      stopOnInteraction: false,
    }),
  );

  // Handle Scrolling on click event
  useEffect(() => {
    if (embla) {
      embla.scrollTo(activeTab);
    }
  }, [activeTab, embla]);

  return (
    <section
      className={clsx("container py-5 lg:py-8 xl:py-[50px]", className)}
      {...other}
    >
      {(heading || description) && (
        <div className="mb-8 text-center lg:mb-12">
          {heading && (
            <h2 className="mb-3 text-h5 font-medium text-black-900 lg:mb-5 lg:text-h3 xl:text-h2">
              {heading}
            </h2>
          )}
          {description && (
            <div className="mx-auto max-w-[976px]">
              <HtmlText className="text-gray-500">{description}</HtmlText>
            </div>
          )}
        </div>
      )}

      <div className="flex w-full flex-col md:flex-row">
        {regulations && regulations.length > 0 && (
          <>
            <div className="mb-5 flex w-full flex-wrap items-start gap-4 md:mb-0 md:max-w-[280px] md:flex-col md:gap-y-10 md:pl-2">
              {/* labels */}
              {regulations.map((rgl, id) => (
                <div
                  key={id}
                  className="whitespace-nowrap"
                  onClick={() => setActiveTab(id)}
                >
                  <li className="flex items-center">
                    <span
                      className={clsx(
                        "size-2.5 rounded-full bg-yellow-400",
                        id === activeTab && "spin compliance-spin",
                      )}
                    ></span>
                    <p
                      className={clsx(
                        "text-md ml-4 cursor-pointer font-normal text-black-400 md:ml-6 md:text-lg",
                        id === activeTab && "!text-primary-900",
                      )}
                      aria-current="page"
                    >
                      {rgl.name}
                    </p>
                  </li>
                </div>
              ))}
            </div>

            {/* section description */}
            <Carousel
              align="start"
              className="md:w-3/5"
              withControls={false}
              onSlideChange={(value) => setActiveTab(value)}
              draggable={false}
              plugins={[autoplay.current]}
              getEmblaApi={setEmbla}
              loop={true}
            >
              {regulations.map((regulation, idx) => (
                <Carousel.Slide key={idx} className="flex flex-row md:px-5">
                  <HtmlText className="prose-h5:!mt-0">
                    {regulation.description}
                  </HtmlText>
                </Carousel.Slide>
              ))}
            </Carousel>
          </>
        )}
      </div>
    </section>
  );
};

export default RegulationSection;
